import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ContentEditor } from '@/components/ui/content-editor';
import { 
  Save, 
  Eye, 
  Send, 
  Plus,
  Trash2,
  Star,
  Globe,
  MapPin,
  Building2,
  Users,
  CheckCircle,
  AlertCircle,
  Clock,
  FileText
} from 'lucide-react';
import { supabase, Location } from '@/lib/supabase';
import slugify from 'slugify';

interface LocationEditorProps {
  location?: Location;
  onSave?: (location: Location) => void;
  onCancel?: () => void;
  onPreview?: (location: Location) => void;
}

interface LocationFormData {
  name: string;
  slug: string;
  description: string;
  custom_title?: string;
  area_description?: string;
  status: 'draft' | 'published' | 'archived';
  is_primary: boolean;
  meta_title?: string;
  meta_description?: string;
  hero_image?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  phone?: string;
  email?: string;
  hours?: string;
  service_areas: string[];
  services_offered: Array<{ id: string; service_name: string; description: string }>;
  faqs: Array<{ id: string; question: string; answer: string }>;
  parent_id?: string;
  franchise_region?: string;
}

const createLocation = async (data: Partial<Location>): Promise<Location> => {
  const { data: result, error } = await supabase
    .from('locations')
    .insert([{
      ...data,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }])
    .select()
    .single();
  
  if (error) throw error;
  return result;
};

const updateLocation = async (id: string, data: Partial<Location>): Promise<Location> => {
  const { data: result, error } = await supabase
    .from('locations')
    .update({
      ...data,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return result;
};

export const LocationEditor: React.FC<LocationEditorProps> = ({
  location,
  onSave,
  onCancel,
  onPreview
}) => {
  const queryClient = useQueryClient();
  const isEditing = !!location;

  const [formData, setFormData] = useState<LocationFormData>({
    name: location?.name || '',
    slug: location?.slug || '',
    description: location?.description || '',
    custom_title: location?.custom_title || '',
    area_description: location?.area_description || '',
    status: location?.status || 'draft',
    is_primary: location?.is_primary || false,
    meta_title: location?.meta_title || '',
    meta_description: location?.meta_description || '',
    hero_image: location?.hero_image || '',
    address_line1: location?.address_line1 || '',
    address_line2: location?.address_line2 || '',
    city: location?.city || '',
    province: location?.province || 'Ontario',
    postal_code: location?.postal_code || '',
    phone: location?.phone || '',
    email: location?.email || '',
    hours: location?.hours || '',
    service_areas: location?.service_areas || [],
    services_offered: location?.services_offered || [],
    faqs: location?.faqs || [],
    parent_id: location?.parent_id || '',
    franchise_region: location?.franchise_region || 'general'
  });

  const [activeTab, setActiveTab] = useState('general');
  const [seoScore, setSeoScore] = useState(85);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Auto-generate slug from name
  useEffect(() => {
    if (formData.name && !isEditing) {
      const newSlug = slugify(formData.name, { lower: true, strict: true });
      setFormData(prev => ({ ...prev, slug: newSlug }));
    }
  }, [formData.name, isEditing]);

  // Auto-generate meta title if not set
  useEffect(() => {
    if (formData.name && !formData.meta_title) {
      setFormData(prev => ({ ...prev, meta_title: `${formData.name} - FixMyBrick Masonry Services` }));
    }
  }, [formData.name, formData.meta_title]);

  // Calculate SEO score
  useEffect(() => {
    let score = 0;
    if (formData.name.length >= 20 && formData.name.length <= 60) score += 20;
    if (formData.meta_description && formData.meta_description.length >= 120 && formData.meta_description.length <= 160) score += 20;
    if (formData.description && formData.description.length >= 100) score += 15;
    if (formData.area_description && formData.area_description.length >= 200) score += 15;
    if (formData.hero_image) score += 10;
    if (formData.services_offered.length >= 3) score += 10;
    if (formData.faqs.length >= 2) score += 10;
    setSeoScore(score);
  }, [formData]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: LocationFormData) => {
      const locationData = {
        ...data,
      };

      if (isEditing && location) {
        return updateLocation(location.id, locationData);
      } else {
        return createLocation(locationData);
      }
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['locations'] });
      if (onSave) onSave(result);
    },
    onError: (error) => {
      console.error('Error saving location:', error);
    }
  });

  const handleInputChange = (
    field: keyof LocationFormData,
    value: LocationFormData[keyof LocationFormData]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSave = async (status?: LocationFormData['status']) => {
    const dataToSave = status ? { ...formData, status } : formData;
    saveMutation.mutate(dataToSave);
  };

  const handlePreview = () => {
    if (onPreview) {
      onPreview({
        ...formData,
        id: location?.id || 'preview',
        created_at: location?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as Location);
    }
  };

  // Services offered management
  const addService = () => {
    const newService = {
      id: Date.now().toString(),
      service_name: '',
      description: ''
    };
    setFormData(prev => ({
      ...prev,
      services_offered: [...prev.services_offered, newService]
    }));
  };

  const updateService = (id: string, field: 'service_name' | 'description', value: string) => {
    setFormData(prev => ({
      ...prev,
      services_offered: prev.services_offered.map(service =>
        service.id === id ? { ...service, [field]: value } : service
      )
    }));
  };

  const removeService = (id: string) => {
    setFormData(prev => ({
      ...prev,
      services_offered: prev.services_offered.filter(service => service.id !== id)
    }));
  };

  // FAQ management
  const addFAQ = () => {
    const newFAQ = {
      id: Date.now().toString(),
      question: '',
      answer: ''
    };
    setFormData(prev => ({
      ...prev,
      faqs: [...prev.faqs, newFAQ]
    }));
  };

  const updateFAQ = (id: string, field: 'question' | 'answer', value: string) => {
    setFormData(prev => ({
      ...prev,
      faqs: prev.faqs.map(faq =>
        faq.id === id ? { ...faq, [field]: value } : faq
      )
    }));
  };

  const removeFAQ = (id: string) => {
    setFormData(prev => ({
      ...prev,
      faqs: prev.faqs.filter(faq => faq.id !== id)
    }));
  };

  const getSeoScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSeoScoreIcon = (score: number) => {
    if (score >= 80) return <CheckCircle className="h-4 w-4 text-green-600" />;
    if (score >= 60) return <Clock className="h-4 w-4 text-yellow-600" />;
    return <AlertCircle className="h-4 w-4 text-red-600" />;
  };

  const franchiseRegions = [
    { value: 'HQ', label: 'Headquarters' },
    { value: 'toronto', label: 'Toronto' },
    { value: 'hamilton', label: 'Hamilton' },
    { value: 'mississauga', label: 'Mississauga' },
    { value: 'brampton', label: 'Brampton' },
    { value: 'north_york', label: 'North York' },
    { value: 'scarborough', label: 'Scarborough' },
    { value: 'london', label: 'London' },
    { value: 'general', label: 'General' }
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEditing ? 'Edit Location' : 'Create New Location'}
          </h1>
          <p className="text-muted-foreground">
            {isEditing ? 'Update your location page content and settings.' : 'Create a comprehensive location page with service area information.'}
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="outline" onClick={handlePreview}>
            <Eye className="mr-2 h-4 w-4" />
            Preview
          </Button>
          <Button 
            variant="outline" 
            onClick={() => handleSave('draft')}
            disabled={saveMutation.isPending}
          >
            <Save className="mr-2 h-4 w-4" />
            Save Draft
          </Button>
          <Button 
            onClick={() => handleSave('published')}
            disabled={saveMutation.isPending}
          >
            <Send className="mr-2 h-4 w-4" />
            Publish
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Main Content Area */}
        <div className="lg:col-span-3">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="general" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                General
              </TabsTrigger>
              <TabsTrigger value="contact" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Contact
              </TabsTrigger>
              <TabsTrigger value="services" className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Services
              </TabsTrigger>
              <TabsTrigger value="areas" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Areas
              </TabsTrigger>
              <TabsTrigger value="faqs" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                FAQs
              </TabsTrigger>
              <TabsTrigger value="seo" className="flex items-center gap-2">
                <Globe className="h-4 w-4" />
                SEO
              </TabsTrigger>
            </TabsList>

            {/* General Tab */}
            <TabsContent value="general" className="space-y-6 mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Basic Information</CardTitle>
                  <CardDescription>
                    Essential details about your service location.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Location Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name">Location Name *</Label>
                    <Input
                      id="name"
                      placeholder="e.g., Toronto Central Office"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      {formData.name.length}/60 characters
                    </p>
                  </div>

                  {/* Slug */}
                  <div className="space-y-2">
                    <Label htmlFor="slug">URL Slug *</Label>
                    <Input
                      id="slug"
                      placeholder="toronto-central"
                      value={formData.slug}
                      onChange={(e) => handleInputChange('slug', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      URL: /locations/{formData.slug}
                    </p>
                  </div>

                  {/* Custom Title */}
                  <div className="space-y-2">
                    <Label htmlFor="custom_title">Custom Page Title</Label>
                    <Input
                      id="custom_title"
                      placeholder="Professional Masonry Services in Toronto"
                      value={formData.custom_title}
                      onChange={(e) => handleInputChange('custom_title', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Appears as the main headline on the location page
                    </p>
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <Label htmlFor="description">Location Description *</Label>
                    <Textarea
                      id="description"
                      placeholder="Brief overview of this location and services for search results..."
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">
                      {formData.description.length}/160 characters (shown in search results)
                    </p>
                  </div>

                  {/* Area Description */}
                  <div className="space-y-2">
                    <ContentEditor
                      label="Service Area Description"
                      description="Detailed information about the service area, coverage, and local expertise."
                      value={formData.area_description || ''}
                      onChange={(content) => handleInputChange('area_description', content)}
                      placeholder="Describe the service area coverage and local expertise..."
                      height={300}
                      showPreview={true}
                    />
                  </div>

                  {/* Location Features */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="is_primary"
                        checked={formData.is_primary}
                        onCheckedChange={(checked) => handleInputChange('is_primary', checked)}
                      />
                      <Label htmlFor="is_primary" className="flex items-center gap-2">
                        <Star className="h-4 w-4" />
                        Primary Location
                      </Label>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Franchise Region</Label>
                      <Select value={formData.franchise_region} onValueChange={(value) => handleInputChange('franchise_region', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select region" />
                        </SelectTrigger>
                        <SelectContent>
                          {franchiseRegions.map(region => (
                            <SelectItem key={region.value} value={region.value}>
                              {region.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Contact Tab */}
            <TabsContent value="contact" className="space-y-6 mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Contact Information</CardTitle>
                  <CardDescription>
                    Address, phone, email, and hours for this location.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="address_line1">Street Address</Label>
                      <Input
                        id="address_line1"
                        placeholder="123 Main Street"
                        value={formData.address_line1}
                        onChange={(e) => handleInputChange('address_line1', e.target.value)}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="address_line2">Address Line 2</Label>
                      <Input
                        id="address_line2"
                        placeholder="Suite 100"
                        value={formData.address_line2}
                        onChange={(e) => handleInputChange('address_line2', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        placeholder="Toronto"
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="province">Province</Label>
                      <Select value={formData.province} onValueChange={(value) => handleInputChange('province', value)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Ontario">Ontario</SelectItem>
                          <SelectItem value="Quebec">Quebec</SelectItem>
                          <SelectItem value="British Columbia">British Columbia</SelectItem>
                          <SelectItem value="Alberta">Alberta</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="postal_code">Postal Code</Label>
                      <Input
                        id="postal_code"
                        placeholder="M1M 1M1"
                        value={formData.postal_code}
                        onChange={(e) => handleInputChange('postal_code', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        placeholder="(416) 555-0123"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="toronto@fixmybrick.com"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="hours">Business Hours</Label>
                    <Textarea
                      id="hours"
                      placeholder="Monday - Friday: 8:00 AM - 6:00 PM&#10;Saturday: 9:00 AM - 4:00 PM&#10;Sunday: Closed"
                      value={formData.hours}
                      onChange={(e) => handleInputChange('hours', e.target.value)}
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Services Tab */}
            <TabsContent value="services" className="space-y-6 mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Services Offered</CardTitle>
                  <CardDescription>
                    Manage the specific services available at this location.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {formData.services_offered.map((service, index) => (
                    <div key={service.id} className="p-4 border rounded-lg space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">Service {index + 1}</h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeService(service.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Service Name</Label>
                        <Input
                          placeholder="e.g., Brick Repair"
                          value={service.service_name}
                          onChange={(e) => updateService(service.id, 'service_name', e.target.value)}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea
                          placeholder="Brief description of this service..."
                          value={service.description}
                          onChange={(e) => updateService(service.id, 'description', e.target.value)}
                          rows={2}
                        />
                      </div>
                    </div>
                  ))}
                  
                  <Button onClick={addService} variant="outline" className="w-full">
                    <Plus className="mr-2 h-4 w-4" />
                    Add Service
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Areas Tab */}
            <TabsContent value="areas" className="space-y-6 mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Service Areas</CardTitle>
                  <CardDescription>
                    Define the geographic areas served by this location.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                    <MapPin className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Service area management</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Geographic service area definition coming soon.
                    </p>
                    <div className="mt-6">
                      <Button variant="outline">
                        <Plus className="mr-2 h-4 w-4" />
                        Add Service Area
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* FAQs Tab */}
            <TabsContent value="faqs" className="space-y-6 mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Location FAQs</CardTitle>
                  <CardDescription>
                    Address common questions about this location and services.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {formData.faqs.map((faq, index) => (
                    <div key={faq.id} className="p-4 border rounded-lg space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">FAQ {index + 1}</h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFAQ(faq.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Question</Label>
                        <Input
                          placeholder="e.g., Do you serve my area?"
                          value={faq.question}
                          onChange={(e) => updateFAQ(faq.id, 'question', e.target.value)}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Answer</Label>
                        <Textarea
                          placeholder="Provide a helpful answer..."
                          value={faq.answer}
                          onChange={(e) => updateFAQ(faq.id, 'answer', e.target.value)}
                          rows={3}
                        />
                      </div>
                    </div>
                  ))}
                  
                  <Button onClick={addFAQ} variant="outline" className="w-full">
                    <Plus className="mr-2 h-4 w-4" />
                    Add FAQ
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* SEO Tab */}
            <TabsContent value="seo" className="space-y-6 mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>SEO Settings</CardTitle>
                  <CardDescription>
                    Optimize this location page for search engines.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Meta Title */}
                  <div className="space-y-2">
                    <Label htmlFor="meta_title">Meta Title</Label>
                    <Input
                      id="meta_title"
                      placeholder="SEO title for search engines..."
                      value={formData.meta_title}
                      onChange={(e) => handleInputChange('meta_title', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      {formData.meta_title?.length || 0}/60 characters
                    </p>
                  </div>

                  {/* Meta Description */}
                  <div className="space-y-2">
                    <Label htmlFor="meta_description">Meta Description</Label>
                    <Textarea
                      id="meta_description"
                      placeholder="Describe this location for search engine results..."
                      value={formData.meta_description}
                      onChange={(e) => handleInputChange('meta_description', e.target.value)}
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">
                      {formData.meta_description?.length || 0}/160 characters
                    </p>
                  </div>

                  {/* Hero Image */}
                  <div className="space-y-2">
                    <Label htmlFor="hero_image">Hero Image URL</Label>
                    <Input
                      id="hero_image"
                      placeholder="https://example.com/image.jpg"
                      value={formData.hero_image}
                      onChange={(e) => handleInputChange('hero_image', e.target.value)}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Publishing Options */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Publishing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* SEO Score */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">SEO Score</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 mb-4">
                {getSeoScoreIcon(seoScore)}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">Overall Score</span>
                    <span className={`font-bold ${getSeoScoreColor(seoScore)}`}>
                      {seoScore}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all ${
                        seoScore >= 80 ? 'bg-green-500' : 
                        seoScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${seoScore}%` }}
                    />
                  </div>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span>Name Length</span>
                  <span className={formData.name.length >= 20 && formData.name.length <= 60 ? 'text-green-600' : 'text-red-600'}>
                    {formData.name.length >= 20 && formData.name.length <= 60 ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Meta Description</span>
                  <span className={formData.meta_description && formData.meta_description.length >= 120 && formData.meta_description.length <= 160 ? 'text-green-600' : 'text-red-600'}>
                    {formData.meta_description && formData.meta_description.length >= 120 && formData.meta_description.length <= 160 ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Area Content</span>
                  <span className={formData.area_description && formData.area_description.length >= 200 ? 'text-green-600' : 'text-red-600'}>
                    {formData.area_description && formData.area_description.length >= 200 ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Hero Image</span>
                  <span className={formData.hero_image ? 'text-green-600' : 'text-red-600'}>
                    {formData.hero_image ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Services (3+)</span>
                  <span className={formData.services_offered.length >= 3 ? 'text-green-600' : 'text-red-600'}>
                    {formData.services_offered.length >= 3 ? '✓' : '✗'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Content Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Content Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span>Services</span>
                <span className="font-medium">{formData.services_offered.length}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>FAQs</span>
                <span className="font-medium">{formData.faqs.length}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Area Content Words</span>
                <span className="font-medium">
                  {formData.area_description ? formData.area_description.replace(/<[^>]*>/g, '').split(/\s+/).filter(word => word.length > 0).length : 0}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LocationEditor;
